/**
 * Location + phone normalisation helpers.
 *
 * The rule: the UI collects what a person knows (country, postal code, a local
 * phone number) and this module turns it into what a CRM needs (ISO2 country,
 * city, region, an E.164 phone number). Never store a half-normalised value.
 */

import { countryByIso, type Country } from "@/data/geo";

export type PostalPlace = {
  city: string;
  region: string;
  regionCode: string | null;
};

const postalCache = new Map<string, PostalPlace[]>();

/**
 * Resolves a postal code to its city / region using the open Zippopotam
 * dataset. Returns an empty list when the country is unsupported or the code
 * is unknown — callers must keep the typed values in that case.
 */
export async function lookupPostal(
  iso2: string,
  postal: string,
): Promise<PostalPlace[]> {
  const country = countryByIso(iso2);
  const code = postal.trim();
  if (!country || !country.postalLookup || code.length < 3) return [];

  const key = `${country.iso2}:${code.toUpperCase()}`;
  const cached = postalCache.get(key);
  if (cached) return cached;

  try {
    const res = await fetch(
      `https://api.zippopotam.us/${country.iso2.toLowerCase()}/${encodeURIComponent(code)}`,
    );
    if (!res.ok) {
      postalCache.set(key, []);
      return [];
    }
    const json = (await res.json()) as {
      places?: Array<{
        "place name"?: string;
        state?: string;
        "state abbreviation"?: string;
      }>;
    };
    const places: PostalPlace[] = (json.places ?? [])
      .map((p) => ({
        city: (p["place name"] ?? "").trim(),
        region: (p.state ?? "").trim(),
        regionCode: (p["state abbreviation"] ?? "").trim() || null,
      }))
      .filter((p) => p.city.length > 0);
    postalCache.set(key, places);
    return places;
  } catch {
    return [];
  }
}

/** Digits only, with any leading zeros or duplicated country code removed. */
export function localPhoneDigits(raw: string, country: Country | null): string {
  let digits = (raw ?? "").replace(/\D+/g, "");
  const dial = country?.dial.replace(/\D+/g, "") ?? "";
  if (dial && digits.startsWith(dial) && digits.length > dial.length + 4) {
    digits = digits.slice(dial.length);
  }
  return digits.replace(/^0+/, "");
}

/** `+<dial><local digits>`, or null when there is nothing usable yet. */
export function toE164(raw: string, iso2: string | null | undefined): string | null {
  const country = countryByIso(iso2);
  const local = localPhoneDigits(raw, country);
  if (!country || local.length < 5) return null;
  return `${country.dial.startsWith("+") ? country.dial : `+${country.dial}`}${local}`;
}

/** Pretty display form: `+91 98765 43210` style grouping. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const m = e164.match(/^(\+\d{1,4})(\d+)$/);
  if (!m) return e164;
  const rest = m[2].replace(/(\d{5})(?=\d)/g, "$1 ");
  return `${m[1]} ${rest}`;
}

/** True when the country expects a postal code from this person. */
export function needsPostal(iso2: string | null | undefined): boolean {
  return countryByIso(iso2)?.hasPostal ?? true;
}
